import { useState, useRef, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface ImageCarouselProps {
  images: Array<{ imageUrl: string }>;
  businessName: string;
  selectedSalon?: {
    address?: string;
    city?: string;
  };
  className?: string;
}

export default function ImageCarousel({
  images,
  businessName,
  selectedSalon,
  className = "",
}: ImageCarouselProps) {
  // Track which images have successfully loaded
  const [validImages, setValidImages] = useState<Array<{ imageUrl: string }>>([]);
  const [imagesChecked, setImagesChecked] = useState(false);

  // Validate images on mount
  useEffect(() => {
    if (!images || images.length === 0) {
      setImagesChecked(true);
      return;
    }

    const checkImages = async () => {
      const validatedImages: Array<{ imageUrl: string }> = [];
      
      await Promise.all(
        images.map((img) => {
          return new Promise<void>((resolve) => {
            const imgElement = new Image();
            imgElement.onload = () => {
              validatedImages.push(img);
              resolve();
            };
            imgElement.onerror = () => {
              resolve();
            };
            imgElement.src = img.imageUrl;
          });
        })
      );

      setValidImages(validatedImages);
      setImagesChecked(true);
    };

    checkImages();
  }, [images]);

  // Carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);
  const [carouselStartX, setCarouselStartX] = useState(0);
  const [carouselScrollLeft, setCarouselScrollLeft] = useState(0);

  const totalImages = validImages.length;

  const scrollToImage = useCallback(
    (index: number) => {
      if (!carouselRef.current) return;

      const carousel = carouselRef.current;
      const imageWidth = carousel.scrollWidth / totalImages;
      carousel.scrollTo({
        left: imageWidth * index,
        behavior: "smooth",
      });
      setCurrentImageIndex(index);
    },
    [totalImages]
  );

  const nextImage = useCallback(() => {
    const nextIndex = (currentImageIndex + 1) % totalImages;
    scrollToImage(nextIndex);
  }, [currentImageIndex, totalImages, scrollToImage]);

  const prevImage = useCallback(() => {
    const prevIndex =
      currentImageIndex === 0 ? totalImages - 1 : currentImageIndex - 1;
    scrollToImage(prevIndex);
  }, [currentImageIndex, totalImages, scrollToImage]);

  // Handle scroll to update current image index
  const handleCarouselScroll = useCallback(() => {
    if (!carouselRef.current || isCarouselDragging) return;

    const carousel = carouselRef.current;
    const imageWidth = carousel.scrollWidth / totalImages;
    const scrollLeft = carousel.scrollLeft;
    const newIndex = Math.round(scrollLeft / imageWidth);

    if (
      newIndex !== currentImageIndex &&
      newIndex >= 0 &&
      newIndex < totalImages
    ) {
      setCurrentImageIndex(newIndex);
    }
  }, [currentImageIndex, totalImages, isCarouselDragging]);

  // Touch/mouse drag functionality
  const handleCarouselMouseDown = (e: React.MouseEvent) => {
    if (!carouselRef.current) return;
    setIsCarouselDragging(true);
    setCarouselStartX(e.pageX - carouselRef.current.offsetLeft);
    setCarouselScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleCarouselMouseMove = (e: React.MouseEvent) => {
    if (!isCarouselDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - carouselStartX) * 2;
    carouselRef.current.scrollLeft = carouselScrollLeft - walk;
  };

  const handleCarouselMouseUp = () => {
    setIsCarouselDragging(false);
    setTimeout(() => handleCarouselScroll(), 100);
  };

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    if (totalImages <= 1) return;

    const interval = setInterval(() => {
      if (!isCarouselDragging) {
        nextImage();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [totalImages, isCarouselDragging, nextImage]);

  // Don't render until images are checked, or if no valid images
  if (!imagesChecked || validImages.length === 0) {
    return null;
  }

  return (
    <div className={`relative group ${className}`}>
      <div
        ref={carouselRef}
        className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory h-100 md:h-80 lg:h-96"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        onScroll={handleCarouselScroll}
        onMouseDown={handleCarouselMouseDown}
        onMouseMove={handleCarouselMouseMove}
        onMouseUp={handleCarouselMouseUp}
        onMouseLeave={handleCarouselMouseUp}
      >
        {validImages.map((img, index) => (
          <div
            key={index}
            className="flex-none w-full h-full snap-start relative"
          >
            <img
              src={img.imageUrl}
              alt={`${businessName} ${index + 1}`}
              className="w-full h-full object-cover lg:object-contain select-none"
              draggable={false}
            />
            {index === currentImageIndex && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:hidden">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h1 className="text-2xl font-bold text-white">
                    {businessName}
                  </h1>
                  {selectedSalon && (
                    <p className="text-white/90 text-sm mt-1">
                      {selectedSalon.address}
                      {selectedSalon.city && `, ${selectedSalon.city}`}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {totalImages > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2    bg-white text-grey-900 rounded-full p-2 shadow-lg transition-all  "
            aria-label="Previous image"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2  bg-white text-grey-900 rounded-full p-2 shadow-lg transition-all "
            aria-label="Next image"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      {/* Scroll Indicators */}
      {/* {totalImages > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/20 backdrop-blur-sm rounded-full px-3 py-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToImage(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentImageIndex
                  ? "bg-white w-6"
                  : "bg-white/50 hover:bg-white/75"
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )} */}

      {/* Image Counter */}
      {totalImages > 1 && (
        <div className="absolute top-4 right-4 bg-black/50 text-white text-sm px-3 py-1 rounded-full backdrop-blur-sm">
          {currentImageIndex + 1} / {totalImages}
        </div>
      )}
    </div>
  );
}
